/*
Loads Cookiebot's uc.js on (main) routes.

The script is injected client-side in an effect instead of being rendered as
JSX. Rendered declaratively, React hoists <script async src> into <head>, where
it races hydration: uc.js appends its banner markup (#CybotCookiebotDialog) to
<body>, and if it wins that race React finds that <div> where it expects the
Next.js metadata <div hidden> and aborts with a hydration mismatch. React then
re-creates the <script> element on the client, where scripts are never executed
— so uc.js would silently stop running at all. Injecting after hydration avoids
both.

Because uc.js therefore runs late, Cookiebot's data-blockingmode="auto" must not
be relied on: every third-party resource has to be gated explicitly. Today all
of them are — the tracking scripts in TrackingCodes via type="text/plain" +
data-cookieconsent, the Instagram embed via a Cookiebot.consent.marketing check
in InstagramPost — and the GCM default state is set even earlier by the
server-rendered CookieConsent component (next/script beforeInteractive in the
root layout). Anything new (iframes for YouTube/Maps/Spotify, external widgets)
needs the same explicit gating instead of auto blocking.

It stays in the (main) layout so the (embedded) WebView routes get no Cookiebot
banner.

More info:
- Cookiebot: https://support.cookiebot.com/hc/en-us/
- Auto blocking: https://support.cookiebot.com/hc/en-us/articles/360009074960-Automatic-cookie-blocking
*/

'use client';

import { useEffect } from 'react';
import '@/styles/vendor/cookiebot.css';

const COOKIEBOT_SCRIPT_ID = 'Cookiebot';
const COOKIEBOT_SRC = 'https://consent.cookiebot.com/uc.js';
const COOKIEBOT_CBID = '977d4895-6345-4253-8a1c-6cc7ec2e8061';

export default function CookiebotLoader() {
  useEffect(() => {
    if (document.getElementById(COOKIEBOT_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = COOKIEBOT_SCRIPT_ID;
    script.src = COOKIEBOT_SRC;
    script.async = true;
    script.setAttribute('data-cbid', COOKIEBOT_CBID);
    script.setAttribute('data-blockingmode', 'auto');
    document.head.appendChild(script);

    // Tear down Cookiebot's injected DOM when leaving the (main) group (e.g. into
    // an embedded WebView route) so no orphaned banner remains.
    return () => {
      script.remove();
      document
        .querySelectorAll('[id^="Cybot"], [id^="Cookiebot"]')
        .forEach((el) => el.remove());
    };
  }, []);

  return null;
}
